import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { isBeneficiary } from 'utils';
import { ReactComponent as SubmitIcon } from 'assets/icons/check.svg';
import { createFundingApplication, updateFormField } from 'store/forms/actions';
import { useInvestorApplicationValidation } from 'hooks/useInvestorApplicationValidation';
import { Steps } from '../../steps';
import styles from './MainFormFooter.module.scss';

/**
 * @component
 * @description
 * **MainForm footer** component for the multi-tab and sub-tab form.
 * Handles navigation between main tabs and sub-tabs.
 * SubTab changed is handled using redux.
 * @param {Object} props - Component props object.
 * @param {number} props.currentTab - The currently active main tab index.
 * @param {Function} props.setCurrentTab - Setter that updates the active main tab.
 * @returns {JSX.Element} The rendered footer with navigation controls.
 */
export function MainFormFooter({ currentTab, setCurrentTab }) {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const state = useSelector(state => state.forms);
  const groups = useSelector(state => state.user.groups);
  const currentSubTab =
    useSelector(state => state.forms.carrier.currentSubTab) || 0;
  const { isValid, logValidation } = useInvestorApplicationValidation();

  const tabMap = useMemo(() => buildTabMap(), []);
  const mainTabs = useMemo(() => Object.keys(tabMap).map(Number), [tabMap]);

  logValidation();

  const getSubTabs = tabId => tabMap[tabId] || null;

  const setCurrentSubTab = value => {
    dispatch(
      updateFormField({
        group: 'carrier',
        key: 'currentSubTab',
        value,
      })
    );
  };

  const subTabs = getSubTabs(currentTab);
  const currentMainTabIndex = mainTabs.indexOf(currentTab);
  const currentSubTabIndex = subTabs ? subTabs.indexOf(currentSubTab) : -1;

  const hasNextSubTab = subTabs && currentSubTabIndex < subTabs.length - 1;
  const hasNextMainTab = currentMainTabIndex < mainTabs.length - 1;
  const hasNextTab = hasNextSubTab || hasNextMainTab;

  const hasPrevSubTab = subTabs && currentSubTabIndex > 0;
  const hasPrevMainTab = currentMainTabIndex > 0;
  const hasPreviousTab = hasPrevSubTab || hasPrevMainTab;

  const goNext = () => {
    if (hasNextSubTab) {
      setCurrentSubTab(subTabs[currentSubTabIndex + 1]);
      return;
    }

    if (hasNextMainTab) {
      const nextTab = mainTabs[currentMainTabIndex + 1];
      setCurrentTab(nextTab);
      const nextSubTabs = getSubTabs(nextTab);
      setCurrentSubTab(nextSubTabs ? nextSubTabs[0] : null);
    }
  };

  const goPrev = () => {
    if (hasPrevSubTab) {
      setCurrentSubTab(subTabs[currentSubTabIndex - 1]);
      return;
    }

    if (hasPrevMainTab) {
      const prevTab = mainTabs[currentMainTabIndex - 1];
      setCurrentTab(prevTab);
      const prevSubTabs = getSubTabs(prevTab);
      setCurrentSubTab(
        prevSubTabs ? prevSubTabs[prevSubTabs.length - 1] : null
      );
    }
  };

  return (
    <div className={styles.formsFooterContainer}>
      <div className={styles.formsFooter}>
        <div className={styles.formsFooterLeft}>
          <button
            className={`${styles.btn} ${styles.purpleBtn}`}
            onClick={() => {
              if (hasPreviousTab) {
                goPrev();
              } else {
                navigate('/fundingpage');
              }
            }}
          >
            {hasPreviousTab ? '← Προηγούμενο' : '← Πίσω στην My Funding'}
          </button>
          {hasNextTab && <button
            className={`${styles.btn} ${styles.purpleBtn}`}
            disabled={!hasNextTab}
            onClick={goNext}
          >
            Επόμενο →
          </button>}
        </div>
        {isBeneficiary(groups) && (
          <div className={styles.formsFooterRight}>
            <button
              className={`${styles.submitCoe} ${
                !isValid ? styles.disabledButton : ''
              }`}
              disabled={!isValid}
              onClick={() => {
                dispatch(createFundingApplication(navigate, state, params.id));
              }}
            >
              <SubmitIcon
                style={{
                  marginRight: 6,
                  color: isValid ? 'white' : 'black',
                }}
              />
              Υποβολή στο CoE
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * @description
 * Builds a mapping between main tabs and their inner sub-tabs.
 * The result is an object where each key is a main tab index
 * and each value is either:
 *   - an array of sub-tab indexes (e.g. [0, 1, 2]), or
 *   - null if that main tab has no sub-tabs.
 *
 * @example
 * {
 *   0: [0, 1, 2],
 *   1: null,
 *   2: [0, 1, 2, 3],
 *   3: null,
 *   4: null
 * }
 *
 * @param {boolean} [parseFlag] - Optional parameter passed into Steps() (if required by the business logic).
 * @returns {Object.<number, number[] | null>} A mapping of main tab → list of sub-tabs.
 */
const buildTabMap = parseFlag => {
  const stepList = Steps(parseFlag);
  const map = {};

  stepList.forEach(step => {
    if (Array.isArray(step.tabsComponent)) {
      const indexArray = step.tabsComponent.map((_, index) => index);
      map[step.id - 1] = indexArray.length > 0 ? indexArray : null;
    } else {
      map[step.id - 1] = null;
    }
  });

  return map;
};
